import { DownloadOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Space,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { getAllExportService } from 'common/services/extends/exports';
import { ExportDataTypes, ExportParamsTypes, ExportPayrollPeriodTypes } from 'common/services/extends/exports/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { downloadFile, formatDateTime, mappingURLToExternal } from 'common/utils/functions';
import ExportSalarySummary from 'extends/ExportSalarySummary';

const ExportSalaryManagement: React.FC = () => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const { defaultPageSize } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);

  const queryKey = ['export-list', currentPage, currentView];

  /* Queries */
  const {
    data: listExportData, isLoading, isFetching, refetch
  } = useQuery(
    queryKey,
    () => getAllExportService({
      page: currentPage,
      limit: currentView,
    }),
  );

  const handleStatusLabel = (status?: string) => {
    switch (status) {
      case 'pending':
        return {
          label: t('exportReports.pending'),
          style: ''
        };
      case 'processing':
        return {
          label: t('exportReports.processing'),
          style: 'approving'
        };
      case 'success':
        return {
          label: t('exportReports.success'),
          style: 'approved'
        };
      case 'failed':
        return {
          label: t('exportReports.failed'),
          style: 'refuse'
        };
      default:
        return null;
    }
  };

  const renderParams = (params?: ExportParamsTypes, payrollPeriod?: ExportPayrollPeriodTypes) => {
    if (!params) return null;
    const items: string[] = [];
    if (params.year) items.push(`${t('exportReports.year')}: ${params.year}`);
    if (params.yearMonth) items.push(`${t('exportReports.yearMonth')}: ${params.yearMonth.split('-').reverse().join('/')}`);
    if (params.payrollPeriodId) {
      items.push(
        `${t('exportReports.payrollPeriodId')}: ${payrollPeriod?.name || `#${params.payrollPeriodId}`}`
      );
      if (payrollPeriod?.codeDetail) {
        items.push(`${t('exportReports.codeDetail')}: ${payrollPeriod.codeDetail}`);
      }
    }
    if (params.filters) {
      const filterEntries = Array.isArray(params.filters) ? [] : Object.entries(params.filters);
      if (filterEntries.length) {
        filterEntries.forEach(([field, value]) => {
          const values = Object.values(value || {}).flat().filter(Boolean);
          items.push(`${t(`filterField.${field}`)}: ${values.join(', ')}`);
        });
      } else {
        items.push('');
      }
    }
    if (!items.length) return null;
    return (
      <Space direction="vertical" size={0}>
        {items.map((item) => (
          <Typography.Text key={item}>{item}</Typography.Text>
        ))}
      </Space>
    );
  };

  const handleDownload = (data: ExportDataTypes) => {
    if (data.status !== 'success' || !data.downloadUrl) return;
    downloadFile(mappingURLToExternal(data.downloadUrl), data.fileName);
  };

  /* Variables */
  const columns: ColumnsType<ExportDataTypes> = [
    {
      title: t('system.code'),
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (_: string, data: ExportDataTypes) => (
        <Typography.Text>
          {data.code}
        </Typography.Text>
      ),
    },
    {
      title: t('exportReports.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 300,
      render: (_: string, data: ExportDataTypes) => (
        <Typography.Text>
          {data.fileName}
        </Typography.Text>
      ),
    },
    {
      title: t('exportReports.infoExportFile'),
      dataIndex: 'params',
      key: 'params',
      width: 220,
      render: (_: ExportParamsTypes, data: ExportDataTypes) => (
        renderParams(data.params, data.payrollPeriod)
      ),
    },
    {
      title: t('system.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (_: string, data: ExportDataTypes) => (
        <Typography.Text className={`export-salary-management__status ${handleStatusLabel(data.status)?.style}`}>
          {handleStatusLabel(data.status)?.label}
        </Typography.Text>
      ),
    },
    {
      title: t('system.preview'),
      dataIndex: 'downloadUrl',
      key: 'downloadUrl',
      width: 110,
      align: 'center',
      render: (_: string, data: ExportDataTypes) => (data.downloadUrl ? (
        <Button
          icon={<FileExcelOutlined />}
          onClick={() => navigate(`${ROUTE_PATHS.PREVIEW_FILE}?code=${data.code}`)}
        >
          {t('system.preview')}
        </Button>
      ) : null),
    },
    {
      title: t('exportReports.download'),
      dataIndex: 'download',
      key: 'download',
      align: 'center',
      width: 110,
      render: (_: string, data: ExportDataTypes) => (data.downloadUrl ? (
        <Button
          icon={<DownloadOutlined />}
          disabled={data.status !== 'success' || !data.downloadUrl}
          onClick={() => handleDownload(data)}
        >
          {t('exportReports.download')}
        </Button>
      ) : null),
    },
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (_: string, data: ExportDataTypes) => (
        <Typography.Text>
          {formatDateTime(data.createdAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (_: string, data: ExportDataTypes) => (
        <Typography.Text>
          {formatDateTime(data.updatedAt)}
        </Typography.Text>
      ),
    }
  ];

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const tableData: ExportDataTypes[] = useMemo(() => {
    if (listExportData?.data) {
      return listExportData.data.map((item) => ({
        code: item.code,
        fileName: item.fileName,
        status: item.status,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        downloadUrl: item.downloadUrl,
        params: item.params,
        payrollPeriod: item.payrollPeriod,
      }));
    }
    return [];
  }, [listExportData]);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.exportReports')}
      />
      <div className="t-mainlayout_wrapper">
        <ExportSalarySummary
          queryKeyTable={queryKey}
        />
        <PageTable
          isLoading={isLoading || isFetching}
          noCheckbox
          leftCustomForm={(
            <Button
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => refetch()}
            >
              {t('media.reload')}
            </Button>
          )}
          tableProps={{
            initShowColumns: ['fileName', 'params', 'status', 'downloadUrl', 'download', 'createdAt'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: listExportData?.meta.total || 1,
            scroll: { x: 1280 },
            noBaseCol: true,
            noDeleteLanguage: true,
          }}
        />
      </div>
    </>
  );
};

export default ExportSalaryManagement;
